// author: Caleb Yearsley (caleb.yearsley@tecuity.com)
// date: 2019-09-19

import "./useMissingTemplate.less";

import { notification, Alert, Button, Select } from "antd";
import { OrderedEntryGroupApi, OrderedListingGroupApi, OrderedSectionApi } from "lib/api";
import { EntryGroup, EntryGroupTemplate, ListingGroup, ListingGroupTemplate, OrderedEntryGroup, OrderedListingGroup, OrderedSection, Section, SectionTemplate, SectionVersion } from "lib/data/model";
import { ComponentTemplateService, UtilityService } from "lib/services";
import { cloneDeep } from "lodash";
import * as React from "react";

interface IMissingTemplateState<T> {
	warning: React.ReactNode;
	component: T;
	isOrphaned: boolean;
	setComponent: React.Dispatch<React.SetStateAction<T>>;
}

export const useMissingTemplate = <T extends OrderedSection | OrderedEntryGroup | OrderedListingGroup>(componentToCheck: T, parentSection: Section, currentSectionVersion: SectionVersion): IMissingTemplateState<T> => {

	const [component, setComponent] = React.useState<T>(componentToCheck);
	const [warning, setWarning] = React.useState<React.ReactNode>(null);
	const [isOrphaned, setIsOrphaned] = React.useState<boolean>(false);
	const [availableSectionTemplate, setAvailableSectionTemplate] = React.useState<Array<SectionTemplate | EntryGroupTemplate | ListingGroupTemplate>>([]);
	const [selectedNewTemplateId, setSelectedNewTemplateId] = React.useState<number | undefined>(undefined);
	const [isSavingNewTemplate, setIsSavingNewTemplate] = React.useState<boolean>(false);

	React.useEffect(() => {

		const orderedSectionTemplateCheck = async () => {

			// Check to see if the current component is missing a template
			let template: SectionTemplate | EntryGroupTemplate | ListingGroupTemplate | undefined;

			switch (component.$type) {

				case OrderedSection.$type:
					template = await OrderedSectionApi.getTemplate(parentSection, component as OrderedSection);
					break;

				case OrderedEntryGroup.$type:
					template = await OrderedEntryGroupApi.getTemplate(parentSection, component as OrderedEntryGroup);
					break;

				case OrderedListingGroup.$type:
					template = await OrderedListingGroupApi.getTemplate(parentSection, component as OrderedListingGroup);
					break;

				default:
					console.error(`Component type of "${component.$type}" is not valid`);
			}

			if (
				parentSection.template !== undefined
				&& (
					template === undefined
					|| ComponentTemplateService.getRequirementFromTemplate(parentSection.template.requirements, template) === undefined
				)
			) {

				// Get Fields From Values
				const existingComponents = [
					...currentSectionVersion.entryGroups,
					...currentSectionVersion.listingGroups,
					...currentSectionVersion.sections
				];

				// Process Templates
				const requirements = await ComponentTemplateService.getAvailableTemplates(parentSection, parentSection.template.requirements, existingComponents);

				// Get available section templates
				const availableTemplates = requirements.reduce<Array<SectionTemplate | EntryGroupTemplate | ListingGroupTemplate>>((acc, requirement) => {

					switch (component.$type) {

						case OrderedSection.$type:
							if (requirement.item.$type === SectionTemplate.$type) {
								return [...acc, requirement.item as SectionTemplate];
							}
							break;

						case OrderedEntryGroup.$type:
							if (requirement.item.$type === EntryGroupTemplate.$type) {
								return [...acc, requirement.item as EntryGroupTemplate];
							}
							break;

						case OrderedListingGroup.$type:
							if (requirement.item.$type === ListingGroupTemplate.$type) {
								return [...acc, requirement.item as ListingGroupTemplate];
							}
							break;

						default:
							console.error(`Component type of "${component.$type}" is not valid`);
					}

					return acc;
				}, []);

				setAvailableSectionTemplate(availableTemplates);
				setIsOrphaned(true);
			}
		};

		orderedSectionTemplateCheck();
	}, [component, currentSectionVersion.entryGroups, currentSectionVersion.listingGroups, currentSectionVersion.sections, parentSection]);

	React.useEffect(() => {

		const setNewTemplate = async () => {

			if (selectedNewTemplateId !== undefined) {

				setIsSavingNewTemplate(true);

				let newOrderedComponent: T | undefined;

				switch (component.$type) {
					case OrderedSection.$type:

						const newSectionTemplate = new SectionTemplate();
						newSectionTemplate.id = selectedNewTemplateId;

						const newSection = new Section();
						newSection.id = component.item.id;
						newSection.template = newSectionTemplate;
						newOrderedComponent = new OrderedSection(component.order, newSection) as T;
						break;

					case OrderedEntryGroup.$type:

						const newEntryGroupTemplate = new EntryGroupTemplate();
						newEntryGroupTemplate.id = selectedNewTemplateId;

						const newEntryGroup = new EntryGroup();
						newEntryGroup.id = component.item.id;
						newEntryGroup.template = newEntryGroupTemplate;
						newEntryGroup.columns = (component as OrderedEntryGroup).item.columns;
						newEntryGroup.name = (component as OrderedEntryGroup).item.name;
						newEntryGroup.entries = (component as OrderedEntryGroup).item.entries;

						newOrderedComponent = new OrderedEntryGroup() as T;
						newOrderedComponent.order = component.order;
						newOrderedComponent.item = newEntryGroup;
						break;

					case OrderedListingGroup.$type:

						const newListingGroupTemplate = new ListingGroupTemplate();
						newListingGroupTemplate.id = selectedNewTemplateId;

						const newListingGroup = new ListingGroup();
						newListingGroup.id = component.item.id;
						newListingGroup.template = newListingGroupTemplate;
						newListingGroup.columns = (component as OrderedListingGroup).item.columns;
						newListingGroup.name = (component as OrderedListingGroup).item.name;
						newListingGroup.listings = (component as OrderedListingGroup).item.listings;

						newOrderedComponent = new OrderedListingGroup() as T;
						newOrderedComponent.order = component.order;
						newOrderedComponent.item = newListingGroup;
						break;
				}

				if (newOrderedComponent !== undefined) {

					newOrderedComponent.id = component.id;

					let updatedSection: T | undefined;

					switch (component.$type) {
						case OrderedSection.$type:
							updatedSection = await UtilityService.asyncTimeout(OrderedSectionApi.update(parentSection, newOrderedComponent as OrderedSection)) as T;
							break;
						case OrderedEntryGroup.$type:
							updatedSection = await UtilityService.asyncTimeout(OrderedEntryGroupApi.save(parentSection, newOrderedComponent as OrderedEntryGroup)) as T;
							break;
						case OrderedListingGroup.$type:
							updatedSection = await OrderedListingGroupApi.update(parentSection, newOrderedComponent as OrderedListingGroup) as T;
							break;
					}

					if (updatedSection !== undefined) {

						const clonedOrderedSection = cloneDeep(component);
						clonedOrderedSection.item.template = updatedSection.item.template;

						setIsOrphaned(false);
						setComponent(clonedOrderedSection);

						notification.success({
							message: "Successfully Set New Template"
						});
					}
					else {

						notification.error({
							message: "Failed to Set New Template"
						});
					}
				}

				setIsSavingNewTemplate(false);
			}
			else {

				notification.error({
					message: "You Must Select a New Template First"
				});
			}
		};

		setWarning(
			<section className="use-missing-template-warning">
				{
					isOrphaned
						?
						(
							<div className="ordered-section-view-warning">
								<Alert
									message="Missing Template"
									description={(
										<div>
											This child component does not adhere to any template. To correct this either delete this child component or assign it a new template.
											This was likely caused by the original template connected to this child component being deleted.
											{
												availableSectionTemplate.length > 0
													?
													(
														<div className="section-template-select-container">

															<Select
																placeholder="Please select a new template"
																value={selectedNewTemplateId}
																onChange={(newVal: number) => setSelectedNewTemplateId(newVal)}
																disabled={isSavingNewTemplate}
															>
																{
																	availableSectionTemplate.map((template) => (
																		<Select.Option
																			key={template.id}
																			value={template.id}
																		>
																			{template.name}
																		</Select.Option>
																	))
																}
															</Select>
															<Button
																className="new-template-button"
																type="primary"
																onClick={setNewTemplate}
																disabled={isSavingNewTemplate}
																loading={isSavingNewTemplate}
															>
																Set As New Template
															</Button>
														</div>
													)
													:
													null
											}
										</div>
									)}
									type="warning"
									showIcon={true}
								/>
							</div>
						)
						:
						null
				}
			</section>
		);
	}, [availableSectionTemplate, component, isOrphaned, isSavingNewTemplate, parentSection, selectedNewTemplateId]);

	return {
		warning,
		component,
		isOrphaned,
		setComponent
	};
};
